import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useUploadGeoJSON,
  uploadGeoJSONSchema,
  type UploadGeoJSONFormData,
} from "@/api/geographic-boundaries";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, AlertCircle, Upload } from "lucide-react";
import { toast } from "sonner";

interface CreatedBoundary {
  name: string;
  code?: string;
}

interface UploadResult {
  message: string;
  createdBoundaries: CreatedBoundary[];
  totalFeatures: number;
}

interface UploadGeoJSONDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadGeoJSONDialog({
  open,
  onOpenChange,
}: UploadGeoJSONDialogProps) {
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const form = useForm<UploadGeoJSONFormData>({
    resolver: zodResolver(uploadGeoJSONSchema),
    defaultValues: {
      geoJsonData: {
        type: "FeatureCollection",
        features: [],
      },
    },
  });

  const uploadMutation = useUploadGeoJSON({
    onSuccess: (result) => {
      setUploadResult(result);
      setFileError(null);
      toast.success(result.message);
      form.reset();
    },
    onError: (error) => {
      console.error("Upload failed:", error);
      const errorMessage = error.message || "Upload failed";
      setFileError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    setFileError(null);
    setUploadResult(null);
    uploadMutation.mutate(values);
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError(null);
    setUploadResult(null);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const result = e.target?.result;
          if (typeof result === "string") {
            const geoJsonData = JSON.parse(result) as unknown;
            // Validate that it's a FeatureCollection before setting
            if (
              typeof geoJsonData === "object" &&
              geoJsonData !== null &&
              "type" in geoJsonData &&
              geoJsonData.type === "FeatureCollection" &&
              "features" in geoJsonData &&
              Array.isArray(geoJsonData.features)
            ) {
              form.setValue(
                "geoJsonData",
                geoJsonData as UploadGeoJSONFormData["geoJsonData"]
              );
              setFileError(null);

              // Show preview info
              const features = (geoJsonData as { features: unknown[] })
                .features;
              toast.info(`Loaded ${features.length} features from file`);
            } else {
              const errorMsg =
                "Invalid GeoJSON format: must be a FeatureCollection with features array";
              console.error(errorMsg);
              setFileError(errorMsg);
            }
          }
        } catch (error) {
          const errorMsg =
            "Invalid JSON file: " +
            (error instanceof Error ? error.message : "Unknown error");
          console.error(errorMsg);
          setFileError(errorMsg);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClose = () => {
    setUploadResult(null);
    setFileError(null);
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload GeoJSON File</DialogTitle>
        </DialogHeader>

        {uploadResult ? (
          <div className="space-y-4">
            <Alert>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>{uploadResult.message}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p className="text-sm font-medium">Upload Summary:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Total features in file: {uploadResult.totalFeatures}</li>
                <li>
                  • Successfully created:{" "}
                  {uploadResult.createdBoundaries.length}
                </li>
                <li>
                  • Skipped or failed:{" "}
                  {uploadResult.totalFeatures -
                    uploadResult.createdBoundaries.length}
                </li>
              </ul>

              {uploadResult.createdBoundaries.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium mb-2">
                    Created Boundaries:
                  </p>
                  <div className="max-h-48 overflow-y-auto">
                    <ul className="text-sm space-y-1">
                      {uploadResult.createdBoundaries.map((boundary, index) => (
                        <li key={index} className="text-gray-600">
                          • {boundary.name}{" "}
                          {boundary.code ? `(${boundary.code})` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button onClick={handleClose}>Close</Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upload File
                  </label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span>{" "}
                          or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">
                          GeoJSON files from Overpass Turbo or other sources
                        </p>
                      </div>
                      <input
                        type="file"
                        accept=".json,.geojson"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {fileError && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{fileError}</AlertDescription>
                    </Alert>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name="geoJsonData"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GeoJSON Data Preview</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder='{"type": "FeatureCollection", "features": [...]}'
                          className="h-64 font-mono text-sm"
                          value={JSON.stringify(field.value, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(
                                e.target.value
                              ) as UploadGeoJSONFormData["geoJsonData"];
                              field.onChange(parsed);
                              setFileError(null);
                            } catch {
                              // Keep current value if JSON is invalid
                            }
                          }}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-1">
                        You can also paste or edit GeoJSON data directly
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Use{" "}
                  <a
                    href="https://overpass-turbo.eu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Overpass Turbo
                  </a>{" "}
                  to export geographic boundaries as GeoJSON, or fetch directly
                  from OpenStreetMap using the relation ID.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    uploadMutation.isPending ||
                    form.watch("geoJsonData").features.length === 0
                  }
                >
                  {uploadMutation.isPending ? "Uploading..." : "Upload GeoJSON"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
